/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useEffect, useState} from 'react';
import {ErrorType, navigate, showDialog, validateEmail} from '@dvrd/dvr-controls';
import {resetPassword} from '../../../../util/requestSender';
import ForgotPassword from './forgotPassword';
import {getNavigationParam} from '../../../../util/navigateUtil';
import {LOGIN_ROUTE} from '../../../../util/constants';
import {useTranslation} from 'react-i18next';

export default function ForgotPasswordController() {
    const {t} = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);

    function onChange(value: string) {
        setEmail(value);
    }

    function onResetError() {
        setError(null);
    }

    function onClickBack() {
        navigate(LOGIN_ROUTE);
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        resetPassword({
            data: {email},
            callback: () => {
                setLoading(false);
                showDialog(
                    t(
                        'auth.forgotPassword.successMessage',
                        'Als het e-mailadres overeenkomt met een gebruiker in onze database, dan is er een e-mail verzonden met instructies om je wachtwoord opnieuw in te stellen. Het kan een aantal minuten duren voordat deze mail aankomt. We raden je aan om ook je spam te controleren.'
                    ),
                    t('auth.forgotPassword.successTitle', 'Wachtwoord opnieuw instellen')
                );
                navigate('/inloggen');
            },
            // A blocked/non-2xx request (e.g. a Cloudflare 403 after the app sat
            // idle) would otherwise never resolve the spinner. Reset loading and
            // show a retryable error instead of hanging.
            errorCallback: () => {
                setLoading(false);
                showDialog(
                    t(
                        'auth.forgotPassword.connectionError',
                        'Verbinding mislukt — controleer je internet en probeer opnieuw.'
                    ),
                    t('auth.forgotPassword.failedTitle', 'Verzenden mislukt')
                );
            }
        });
    }

    function validateValues() {
        let valid: boolean = true;
        let error: string = '';
        if (!email) {
            valid = false;
            error = t('auth.forgotPassword.emailRequired', 'Vul je e-mailadres in');
        } else if (!validateEmail(email)) {
            valid = false;
            error = t('auth.forgotPassword.emailInvalid', 'Vul een geldig e-mailadres in');
        }
        if (!valid) setError(error);
        return valid;
    }

    useEffect(() => {
        const navParam = getNavigationParam();
        if (navParam && typeof navParam === 'string') setEmail(navParam);
    }, []);

    return (
        <ForgotPassword
            onChange={onChange}
            onSubmit={onSubmit}
            onResetError={onResetError}
            onClickBack={onClickBack}
            email={email}
            error={error}
            loading={loading}
        />
    );
}
